'use client';
import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import leftImg from '../../asset/images/aboutus.png';

const QualitySection = () => {
  // Animation Controllers
  const controlsLeft = useAnimation();
  const controlsRight = useAnimation();
  const [ref, inView] = useInView({ triggerOnce: false, threshold: 0.2 });

  useEffect(() => {
    if (inView) {
      controlsLeft.start('show');
      controlsRight.start('show');
    } else {
      controlsLeft.start('hidden');
      controlsRight.start('hidden');
    }
  }, [inView, controlsLeft, controlsRight]);

  // Animation Variants
  const fadeInLeft = {
    hidden: { opacity: 0, x: -100, scale: 0.9 },
    show: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 100 },
    show: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        when: 'beforeChildren',
        staggerChildren: 0.15,
      },
    },
  };

  const fadeInItem = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <section className="bg-[#1b2f5a] py-10 px-4" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Image on Left */}
          <motion.div
            className="lg:w-1/2"
            variants={fadeInLeft}
            initial="hidden"
            animate={controlsLeft}
          >
            <div className="relative overflow-hidden rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500">
              <img
                src={leftImg}
                alt="Company Quality"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20"></div>
            </div>
          </motion.div>

          {/* Content on Right */}
          <motion.div
            className="lg:w-1/2 text-center lg:text-left"
            variants={fadeInRight}
            initial="hidden"
            animate={controlsRight}
          >
            <motion.p className="font-semibold text-sm uppercase tracking-wider mb-3" style={{ color: '#f7941d' }}>
              Excellence
            </motion.p>
            <motion.h2
              className="text-4xl lg:text-5xl font-bold mb-6 text-gray-50 leading-tight"
              variants={fadeInItem}
            >
              We Deliver Quality
            </motion.h2>

            <motion.div
              className="w-16 h-1 mt-4"
              style={{ backgroundColor: '#f7941d' }}
              variants={fadeInItem}
            ></motion.div>

            <motion.p
              className="text-xl text-gray-300 leading-relaxed mb-8 mt-8"
              variants={fadeInItem}
            >
              Our mission is to provide top-notch technology services that exceed expectations and drive meaningful results. From initial idea to final implementation, we focus on excellence, innovation, and delivering solutions that truly make a difference for our clients.
            </motion.p>

            {/* Feature Points */}
            <div className="space-y-4">
              {[
                'Excellence in every project',
                'Innovation-driven solutions',
                'Client-focused approach',
              ].map((point, idx) => (
                <motion.div
                  key={idx}
                  className="flex items-center justify-center lg:justify-start gap-3"
                  variants={fadeInItem}
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: '#F7941C' }}
                  ></div>
                  <span className="text-gray-300">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default QualitySection;
